import { supabase } from './supabase';
import { getResourceByAuthId } from './resources';

export interface ProjectMeasurement {
  id: string;
  project_id: string | null;
  field_quote_id?: string | null;
  name: string;
  category?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  area?: number | null;
  custom_fields?: Record<string, number | string | { value: number | string; unit?: string }> | null; // Support both old and new format
  unit: string;
  notes?: string | null;
  resource_id?: string | null; // The resource who created this measurement (null for admins)
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export interface AddProjectMeasurementParams {
  /** Owning project. Either project_id or field_quote_id must be set. */
  project_id?: string | null;
  /** Owning field quote. Either project_id or field_quote_id must be set. */
  field_quote_id?: string | null;
  name: string;
  category?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  area?: number | null;
  custom_fields?: Record<string, number | string | { value: number | string; unit?: string }> | null; // Support both old and new format
  unit?: string;
  notes?: string | null;
}

export interface UpdateProjectMeasurementParams {
  name?: string;
  category?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  area?: number | null;
  custom_fields?: Record<string, number | string | { value: number | string; unit?: string }> | null; // Support both old and new format
  unit?: string;
  notes?: string | null;
}

/**
 * Get all measurements for a project
 */
export async function getProjectMeasurements(projectId: string): Promise<ProjectMeasurement[]> {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  const { data, error } = await supabase
    .from('project_measurements')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching project measurements:', error);
    throw error;
  }

  return (data || []) as ProjectMeasurement[];
}

/**
 * Get all measurements for a field quote
 */
export async function getFieldQuoteMeasurements(fieldQuoteId: string): Promise<ProjectMeasurement[]> {
  if (!fieldQuoteId) {
    throw new Error('Field quote ID is required');
  }

  const { data, error } = await supabase
    .from('project_measurements')
    .select('*')
    .eq('field_quote_id', fieldQuoteId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching field quote measurements:', error);
    throw error;
  }

  return (data || []) as ProjectMeasurement[];
}

/**
 * Add a measurement to a project or a field quote (exactly one parent required)
 */
export async function addProjectMeasurement(params: AddProjectMeasurementParams): Promise<ProjectMeasurement> {
  if (!params.project_id && !params.field_quote_id) {
    throw new Error('A project ID or field quote ID is required');
  }
  if (!params.name || !params.name.trim()) {
    throw new Error('Measurement name is required');
  }

  // Get current user for created_by
  const { data: { user } } = await supabase.auth.getUser();

  // Get resource_id if user is a resource (null for admins)
  let resourceId: string | null = null;
  if (user?.id) {
    const resource = await getResourceByAuthId(user.id);
    resourceId = resource?.id || null;
  }

  const { data, error } = await supabase
    .from('project_measurements')
    .insert({
      project_id: params.project_id || null,
      field_quote_id: params.field_quote_id || null,
      name: params.name.trim(),
      category: params.category || null,
      length: params.length || null,
      width: params.width || null,
      height: params.height || null,
      area: params.area || null,
      custom_fields: params.custom_fields || {},
      unit: params.unit || 'ft',
      notes: params.notes || null,
      resource_id: resourceId,
      created_by: user?.id || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding project measurement:', error);
    // Provide more helpful error message for 404 (table doesn't exist)
    if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('not found')) {
      throw new Error('The project_measurements table does not exist. Please run the migration: supabase/migrations/20250120000015_create_project_measurements_table.sql');
    }
    throw error;
  }

  return data as ProjectMeasurement;
}

/**
 * Update a measurement
 */
export async function updateProjectMeasurement(
  measurementId: string,
  params: UpdateProjectMeasurementParams
): Promise<ProjectMeasurement> {
  if (!measurementId) {
    throw new Error('Measurement ID is required');
  }

  const updateData: any = {};
  if (params.name !== undefined) updateData.name = params.name.trim();
  if (params.category !== undefined) updateData.category = params.category;
  if (params.length !== undefined) updateData.length = params.length;
  if (params.width !== undefined) updateData.width = params.width;
  if (params.height !== undefined) updateData.height = params.height;
  if (params.area !== undefined) updateData.area = params.area;
  if (params.custom_fields !== undefined) updateData.custom_fields = params.custom_fields;
  if (params.unit !== undefined) updateData.unit = params.unit;
  if (params.notes !== undefined) updateData.notes = params.notes;

  const { data, error } = await supabase
    .from('project_measurements')
    .update(updateData)
    .eq('id', measurementId)
    .select()
    .single();

  if (error) {
    console.error('Error updating project measurement:', error);
    throw error;
  }

  return data as ProjectMeasurement;
}

/**
 * Delete a measurement
 */
export async function deleteProjectMeasurement(measurementId: string): Promise<void> {
  if (!measurementId) {
    throw new Error('Measurement ID is required');
  }

  const { error } = await supabase
    .from('project_measurements')
    .delete()
    .eq('id', measurementId);

  if (error) {
    console.error('Error deleting project measurement:', error);
    throw error;
  }
}

/**
 * Helper function to get the display value for a custom field
 * Supports both old format (direct value) and new format ({ value, unit })
 */
export function getCustomFieldValue(fieldValue: any): number | string {
  // NEW FORMAT: If the value is an object with a value property, extract it
  if (typeof fieldValue === 'object' && fieldValue !== null && 'value' in fieldValue) {
    return fieldValue.value;
  }
  // OLD FORMAT: Return the value as-is
  return fieldValue;
}

/**
 * Helper function to get the unit for a custom field
 * Supports both old format (no unit stored) and new format ({ value, unit })
 */
export function getCustomFieldUnit(fieldValue: any, fallbackUnit: string = ''): string {
  // NEW FORMAT: If the value is an object with a unit property, use that
  if (typeof fieldValue === 'object' && fieldValue !== null && 'unit' in fieldValue && fieldValue.unit) {
    return fieldValue.unit;
  }
  // OLD FORMAT or no unit: Return fallback
  return fallbackUnit;
}

/**
 * Calculate area from length and width
 */
export function calculateArea(length: number | null, width: number | null): number | null {
  if (length && width && length > 0 && width > 0) {
    return Math.round((length * width) * 100) / 100;
  }
  return null;
}
